import React, { useState, useEffect, useMemo } from 'react';
import { X, Loader2, Search, ArrowDown, ArrowUp } from 'lucide-react';
import { fetchBuyerEngagement } from '../api/analyticsApi';
import { formatMelbourneShortDate } from '../../../utils/melbourneTime';

/**
 * The buyers behind an Overview card (Monday 13391600148). Each card opens the
 * same list on the population it counted, with a split that explains the rate:
 * returning vs new for Returning Users, enquired or not for Conversion Rate.
 * Save Rate opens PropertySavesModal instead: the saves themselves, not buyers. The backend filters the population exactly as the dashboard does, so
 * the totals here match the card.
 */
export const ENGAGEMENT_VIEWS = {
    unique_buyers: {
        title: 'Active Buyers',
        segment: 'active',
        description: 'Signed-in buyers with at least one event in this range.',
    },
    avg_events: {
        title: 'Avg Events per Buyer',
        segment: 'active',
        description: 'Every active buyer, most active first.',
    },
    returning_buyers: {
        title: 'Returning Users',
        segment: 'active',
        description: 'Active buyers who were also active before this range.',
        split: { label: 'Returning', otherLabel: 'New', test: (b) => b.is_returning },
    },
    conversion_rate: {
        title: 'Conversion Rate',
        segment: 'signups',
        description: 'Buyers who signed up in this range, and whether they sent an enquiry. '
            + 'The card divides all enquiries by signups, so it can differ from this split.',
        split: { label: 'Enquired', otherLabel: "Didn't enquire", test: (b) => b.enquiries > 0 },
    },
};

const COLUMNS = [
    { key: 'events', label: 'Events' },
    { key: 'property_views', label: 'Property Views' },
    { key: 'saves', label: 'Saves' },
    { key: 'enquiries', label: 'Enquiries' },
    { key: 'last_active', label: 'Last Active' },
];

const formatDate = (timestamp) => (timestamp ? formatMelbourneShortDate(timestamp) : '—');

const BuyerEngagementModal = ({ isOpen, onClose, viewId, startDate, endDate, onBuyerClick }) => {
    const view = ENGAGEMENT_VIEWS[viewId];
    const [loading, setLoading] = useState(false);
    const [buyers, setBuyers] = useState([]);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    // 'all' | 'in' | 'out' — which side of the view's split is showing.
    const [splitFilter, setSplitFilter] = useState('all');
    const [sort, setSort] = useState({ key: 'events', desc: true });

    useEffect(() => {
        if (!isOpen || !view) return;
        let cancelled = false;
        setSearchTerm('');
        setSplitFilter(view.split ? 'in' : 'all');
        setSort({ key: 'events', desc: true });
        setLoading(true);
        setError(null);
        fetchBuyerEngagement(view.segment, startDate, endDate)
            .then((data) => { if (!cancelled) setBuyers(data?.buyers || []); })
            .catch(() => { if (!cancelled) setError('Failed to load buyers'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [isOpen, viewId, startDate, endDate]); // eslint-disable-line react-hooks/exhaustive-deps

    const inSplitCount = useMemo(
        () => (view?.split ? buyers.filter(view.split.test).length : 0),
        [buyers, view],
    );

    const visibleBuyers = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        const rows = buyers.filter((b) => {
            if (view?.split && splitFilter !== 'all') {
                if (view.split.test(b) !== (splitFilter === 'in')) return false;
            }
            if (!term) return true;
            return (
                b.name?.toLowerCase().includes(term)
                || b.email?.toLowerCase().includes(term)
                || b.phone?.toLowerCase().includes(term)
            );
        });
        const dir = sort.desc ? -1 : 1;
        return rows.sort((a, b) => {
            const av = sort.key === 'last_active' ? (a.last_active || '') : a[sort.key];
            const bv = sort.key === 'last_active' ? (b.last_active || '') : b[sort.key];
            if (av === bv) return 0;
            return av > bv ? dir : -dir;
        });
    }, [buyers, searchTerm, splitFilter, sort, view]);

    if (!isOpen || !view) return null;

    const toggleSort = (key) => setSort((s) => ({ key, desc: s.key === key ? !s.desc : true }));
    const splitPct = buyers.length ? (inSplitCount / buyers.length) * 100 : 0;
    const filterChips = view.split ? [
        { id: 'in', label: view.split.label, count: inSplitCount },
        { id: 'out', label: view.split.otherLabel, count: buyers.length - inSplitCount },
        { id: 'all', label: 'All', count: buyers.length },
    ] : [];

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-start justify-between p-6 border-b border-gray-200">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">{view.title}</h2>
                        <p className="text-sm text-gray-600 mt-1">{view.description}</p>
                        {!loading && !error && view.split && (
                            <p className="text-sm text-gray-900 font-medium mt-2">
                                {inSplitCount.toLocaleString()} of {buyers.length.toLocaleString()}{' '}
                                ({(Math.round(splitPct * 10) / 10).toFixed(1)}%) {view.split.label.toLowerCase()}
                            </p>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="w-6 h-6 text-gray-500" />
                    </button>
                </div>

                {/* Filters */}
                <div className="px-6 pt-4 flex flex-col md:flex-row gap-3 md:items-center">
                    {filterChips.length > 0 && (
                        <div className="flex gap-2 flex-wrap">
                            {filterChips.map((chip) => (
                                <button
                                    key={chip.id}
                                    onClick={() => setSplitFilter(chip.id)}
                                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${splitFilter === chip.id
                                        ? 'bg-blue-600 text-white border-blue-600'
                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                        }`}
                                >
                                    {chip.label} ({chip.count.toLocaleString()})
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by name, email or phone..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="flex items-center justify-center h-40">
                            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                        </div>
                    ) : error ? (
                        <div className="text-center text-red-600 py-8">{error}</div>
                    ) : visibleBuyers.length === 0 ? (
                        <div className="text-center text-gray-500 py-8">
                            {searchTerm ? 'No buyers found matching your search' : 'No buyers in this range'}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Buyer
                                        </th>
                                        {COLUMNS.map((col) => (
                                            <th
                                                key={col.key}
                                                onClick={() => toggleSort(col.key)}
                                                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700 whitespace-nowrap"
                                            >
                                                <span className="inline-flex items-center gap-1">
                                                    {col.label}
                                                    {sort.key === col.key && (sort.desc
                                                        ? <ArrowDown className="w-3 h-3" />
                                                        : <ArrowUp className="w-3 h-3" />)}
                                                </span>
                                            </th>
                                        ))}
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Type
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {visibleBuyers.map((buyer) => (
                                        <tr
                                            key={buyer.id}
                                            onClick={() => onBuyerClick?.(buyer)}
                                            className={`transition-colors ${onBuyerClick ? 'cursor-pointer hover:bg-blue-50' : 'hover:bg-gray-50'}`}
                                            title={onBuyerClick ? 'Open buyer profile' : undefined}
                                        >
                                            <td className="px-4 py-3">
                                                <div className="text-sm font-medium text-gray-900">{buyer.name}</div>
                                                <div className="text-xs text-gray-500">
                                                    {buyer.email}{buyer.phone ? ` · ${buyer.phone}` : ''}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900">{buyer.events.toLocaleString()}</td>
                                            <td className="px-4 py-3 text-sm text-gray-700">{buyer.property_views.toLocaleString()}</td>
                                            <td className="px-4 py-3 text-sm text-gray-700">{buyer.saves.toLocaleString()}</td>
                                            <td className="px-4 py-3 text-sm text-gray-700">{buyer.enquiries.toLocaleString()}</td>
                                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatDate(buyer.last_active)}</td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${buyer.is_returning
                                                    ? 'bg-blue-100 text-blue-700'
                                                    : 'bg-green-100 text-green-700'
                                                    }`}>
                                                    {buyer.is_returning ? 'Returning' : 'New'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
                    <div className="text-sm text-gray-600">
                        Showing {visibleBuyers.length.toLocaleString()} of {buyers.length.toLocaleString()} buyer{buyers.length !== 1 ? 's' : ''}
                    </div>
                    <button
                        onClick={onClose}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BuyerEngagementModal;
